import { useEffect, useMemo, useRef } from 'react'

import {
  DIGITANE_VIDEO_SITE_ID,
  OPENING_CHAPTER_SEQ,
  OPENING_START_TIMEOUT_MS,
} from '../digitaneVideo/config'
import type {
  DigitaneVideoEventHandlers,
  DigitaneVideoInitOptions,
} from '../digitaneVideo/types'
import { useDigitaneVideo } from '../digitaneVideo/useDigitaneVideo'
import { useProject } from '../project/hook'

interface OpeningVideoProps {
  readonly missionId: number
  readonly isOnlineUser: boolean
  /** 動画の再生が終わった */
  readonly onEnded: () => void
  /** Widget（embed.js）を読み込めなかった・埋め込めなかった */
  readonly onLoadError: (error: Error) => void
  /** 初期化（embed.js の読み込みの開始）から一定時間、再生が始まらなかった */
  readonly onStartTimeout: () => void
}

/**
 * オープニング動画を digitane 動画 Widget（fixed モード）で再生する。
 *
 * オープニングはチュートリアルミッションの seq 0 のチャプター。クリアの記録
 * （80% 視聴時のクリア API 呼び出し）は Widget が行うため、ここでは呼ばない。
 *
 * embed.js の読み込みが応答もエラーも返さずに止まるときや、Widget の中の再生
 * エラーは親へ通知されないため、初期化（embed.js の読み込みの開始）から
 * OPENING_START_TIMEOUT_MS たっても再生が始まらなければ onStartTimeout で
 * 知らせる（video:started / video:resumed が届いたら再生が始まったとみなす）。
 * 読み込みが終わって Widget を埋め込んだ時点では、数え直さない。
 */
export const OpeningVideo = ({
  missionId,
  isOnlineUser,
  onEnded,
  onLoadError,
  onStartTimeout,
}: OpeningVideoProps) => {
  const { updateChapters } = useProject()
  const targetRef = useRef<HTMLDivElement>(null)
  // 再生が始まった（video:started / video:resumed が届いた）か
  const hasStartedRef = useRef(false)

  const options = useMemo<DigitaneVideoInitOptions>(
    () => ({
      missionId,
      chapterSeq: OPENING_CHAPTER_SEQ,
      mode: 'fixed',
      autoplay: true,
      autoResize: false,
      siteId: DIGITANE_VIDEO_SITE_ID,
      isOnlineUser,
    }),
    [missionId, isOnlineUser],
  )

  const markStarted = () => {
    hasStartedRef.current = true
  }

  const handlers: DigitaneVideoEventHandlers = {
    'video:started': markStarted,
    'video:resumed': markStarted,
    'video:ended': () => {
      onEnded()
    },
    // Widget がクリアを記録した後、robcratch 側の
    // missionInfo.chapters[].currentChapterResult を取り直す
    'chapter:cleared': () => {
      void updateChapters()
    },
  }

  const { status, error } = useDigitaneVideo({ targetRef, options, handlers })

  // 親が毎回新しい関数を渡しても、エラー 1 回につき 1 回だけ通知する
  const onLoadErrorRef = useRef(onLoadError)
  onLoadErrorRef.current = onLoadError

  useEffect(() => {
    if (status === 'error' && error !== null) {
      onLoadErrorRef.current(error)
    }
  }, [status, error])

  // embed.js の読み込みが応答もエラーも返さずに止まる場合（status が loading の
  // まま）や、Widget の中の再生エラー（未ログイン、CORS、署名 API の 404、動画
  // の未配置など）は親へ通知されない。初期化（embed.js の読み込みの開始）から
  // 一定時間たっても再生が始まらなければ、利用者がモーダルで止まらないよう
  // 親へ知らせる。
  // onLoadError と同じく、親が毎回新しい関数を渡してもタイマーは張り直さず、
  // 満了時に最新のものを 1 回だけ呼ぶ
  const onStartTimeoutRef = useRef(onStartTimeout)
  onStartTimeoutRef.current = onStartTimeout

  // 埋め込み中（loading / embedded）の間だけタイマーを動かす。loading から
  // embedded に変わっても isEmbedding は変わらず、タイマーは張り直さない
  // （埋め込みの開始から数える）。error（onLoadError が先へ進むボタンを出す）
  // と idle では止める。Widget を作り直すとき（options の中身が変わったとき）は
  // optionsKey が変わるので、数え直す
  const optionsKey = JSON.stringify(options)
  const isEmbedding = status === 'loading' || status === 'embedded'

  useEffect(() => {
    if (!isEmbedding) return

    const timer = setTimeout(() => {
      if (!hasStartedRef.current) onStartTimeoutRef.current()
    }, OPENING_START_TIMEOUT_MS)
    return () => {
      clearTimeout(timer)
      // Widget を作り直すときに、前の Widget の再生記録を持ち越さない
      hasStartedRef.current = false
    }
  }, [isEmbedding, optionsKey])

  return (
    <div className="opening-video">
      <div ref={targetRef} className="opening-video-frame" />
      {status === 'error' && (
        <p className="opening-video-error">動画を読み込めませんでした</p>
      )}
    </div>
  )
}
